import { Controller } from "@hotwired/stimulus"
import RequestHelper from "../helpers/request_helper"

const UPDATE_CONTRACT_TERM = "update_contract_term";

// Mounted twice on admin/organizations/platform_licenses/show.html.haml, once per modal.
// `editModeValue` is "gift_sends" or "contract_term", and _edit.html.haml renders only one mode's
// fields into each modal, so every target below exists only in the instance whose mode renders it
// - and every method that reads one is itself gated on `editModeValue`, so a target is never read
// from the instance that doesn't have it.
//
// `organization_platform_license_gift_sends`/`_contract_start_date`/`_contract_end_date` are
// simple_form-generated ids, unique on the page since only one mode's fields render at a time, and
// `#gift_sends`/`#contract_term` on the page itself sit outside either modal - all four stay
// `document.getElementById` lookups rather than targets.
//
// updateUI() below closes the modal and refreshes the activity table through the `datatable`
// controller's public `reload()`, reached with getControllerForElementAndIdentifier.
export default class extends Controller {
  static get targets() {
    return [
      "contractStartDateValidationMessage",
      "contractEndDateValidationMessage",
      "editContractTermForm",
      "editGiftSendsForm",
      "giftSendsValidationMessage",
      "reasonForm",
      "reasonMessage",
      "reasonMessageValidationMessage",
    ];
  }

  static get values() {
    return { editMode: String, activityContainerId: String };
  }

  connect() {
    if (this.editModeValue === "contract_term") {
      this.connectContractTermFields();
    }
  }

  disconnect() {
    if (this.$contractStartDate) { this.$contractStartDate.off("changeDate.platformLicenseEdit change.platformLicenseEdit"); }
    if (this.$contractEndDate) { this.$contractEndDate.off("change.platformLicenseEdit"); }
  }

  onSaveClick() {
    if (this.isValid()) {
      this.update();
    }
  }

  // The bootstrap datepicker plugin owns `$contractStartDate`/`$contractEndDate`, so both stay
  // jQuery for their whole lifecycle rather than splitting the plugin call from the plain "change"
  // listener onto two different element handles.
  connectContractTermFields() {
    this.$contractStartDate = $(document.getElementById("organization_platform_license_contract_start_date"));
    this.$contractEndDate = $(document.getElementById("organization_platform_license_contract_end_date"));

    this.$contractStartDate.datepicker().on("changeDate.platformLicenseEdit", (e) => {
      const selectedDate = e.date;
      const newEndDate = new Date(selectedDate.getFullYear() + 1, selectedDate.getMonth(), selectedDate.getDate());

      this.$contractEndDate.datepicker("setDate", newEndDate);
      $(e.currentTarget).datepicker("hide");
    });

    this.$contractStartDate.on("change.platformLicenseEdit", () => {
      window.ltFormValidators.hideValidationErrorMessage($(this.contractStartDateValidationMessageTarget));
    });

    this.$contractEndDate.on("change.platformLicenseEdit", () => {
      window.ltFormValidators.hideValidationErrorMessage($(this.contractEndDateValidationMessageTarget));
    });
  }

  onGiftSendsChange() {
    window.ltFormValidators.hideValidationErrorMessage($(this.giftSendsValidationMessageTarget));
  }

  onReasonMessageChange() {
    window.ltFormValidators.hideValidationErrorMessage($(this.reasonMessageValidationMessageTarget));
  }

  getSelectedReason() {
    const checked = this.reasonFormTarget.querySelector('input[name="reason"]:checked');
    return checked ? checked.value : undefined;
  }

  isValid() {
    return this.editModeValue === "gift_sends" ? this.isGiftSendsFormValid() : this.isContractTermsFormValid();
  }

  update() {
    if (this.editModeValue === "gift_sends") {
      this.updateGiftSends();
    } else {
      this.updateContractTerms();
    }
  }

  isGiftSendsFormValid() {
    let valid = true;

    const giftSends = document.getElementById("organization_platform_license_gift_sends").value;
    const reasonMessage = this.reasonMessageTarget.value;
    const selectedReason = this.getSelectedReason();

    if (window.ltFormValidators.isBlank(giftSends) || !window.ltFormValidators.isInteger(giftSends)) {
      window.ltFormValidators.showValidationErrorMessage($(this.giftSendsValidationMessageTarget));
      valid = false;
    }

    if (selectedReason === "other" && window.ltFormValidators.isBlank(reasonMessage)) {
      window.ltFormValidators.showValidationErrorMessage($(this.reasonMessageValidationMessageTarget));
      valid = false;
    }

    return valid;
  }

  isContractTermsFormValid() {
    let valid = true;

    const start = document.getElementById("organization_platform_license_contract_start_date").value;
    const end = document.getElementById("organization_platform_license_contract_end_date").value;

    if (window.ltFormValidators.isBlank(start)) {
      window.ltFormValidators.showValidationErrorMessage($(this.contractStartDateValidationMessageTarget));
      valid = false;
    }

    if (window.ltFormValidators.isBlank(end)) {
      window.ltFormValidators.showValidationErrorMessage($(this.contractEndDateValidationMessageTarget));
      valid = false;
    }

    return valid;
  }

  updateGiftSends() {
    const url = this.editGiftSendsFormTarget.getAttribute("action");
    const giftSends = Number(document.getElementById("organization_platform_license_gift_sends").value);
    const reason = this.getSelectedReason();
    const reasonMessage = this.reasonMessageTarget.value;

    const options = RequestHelper.defaultPatchOptions();
    options.headers = Object.assign({}, options.headers, { "Content-Type": "application/json" });
    options.body = JSON.stringify({
      organization_platform_license: { gift_sends: giftSends },
      update_type: reason,
      update_type_message: reasonMessage,
    });

    this.submit(url, options);
  }

  updateContractTerms() {
    const url = this.editContractTermFormTarget.getAttribute("action");
    const startDate = document.getElementById("organization_platform_license_contract_start_date").value;
    const endDate = document.getElementById("organization_platform_license_contract_end_date").value;

    const options = RequestHelper.defaultPatchOptions();
    options.headers = Object.assign({}, options.headers, { "Content-Type": "application/json" });
    options.body = JSON.stringify({
      organization_platform_license: { contract_start_date: startDate, contract_end_date: endDate },
      update_type: UPDATE_CONTRACT_TERM,
    });

    this.submit(url, options);
  }

  // fetch does not reject on a non-2xx status, so the explicit `response.ok` check is what stops
  // updateUI running against an error body's `{ message: ... }` shape.
  submit(url, options) {
    fetch(url, options).
      then((response) => {
        if (!response.ok) { throw new Error("platform license update failed"); }
        return response.json();
      }).
      then((data) => this.updateUI(data)).
      catch((e) => console.log(e));
  }

  updateUI(data) {
    document.getElementById("gift_sends").value = data.gift_sends;
    document.getElementById("contract_term").value = data.contract_term;

    $(this.element).modal("hide");
    this.reloadActivityTable();
  }

  // The container is a page-level sibling of both this modal and `.platform-license`, so it is
  // reached by id rather than as a target.
  reloadActivityTable() {
    const container = document.getElementById(this.activityContainerIdValue);
    if (!container) { return; }

    const datatable = this.application.getControllerForElementAndIdentifier(container, "datatable");
    if (datatable) { datatable.reload(); }
  }
}
